import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, ErrorText, Field, Spinner, Textarea } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { formatDateTime, formatRut } from '../../utils/format'
import type { Encounter } from '../../types/api'
import { AnamnesisForm } from './AnamnesisForm'
import { DiagnosesSection } from './DiagnosesSection'
import { ClinicalOrdersSection } from './ClinicalOrdersSection'
import { FollowUpsSection } from '../../components/clinical/FollowUpsSection'
import { PatientDocumentsSection } from '../../components/documents/PatientDocumentsSection'

/**
 * Atención clínica (ficha del encuentro). Es la pantalla donde el profesional
 * registra lo que pasó en el box: anamnesis con plantilla (H2), evolución e
 * indicaciones. El acceso queda auditado en el backend (Ley 20.584).
 */
export function EncounterPage() {
  const { id = '' } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [form, setForm] = useState({ reason: '', diagnosis: '', evolution: '', indications: '' })

  const encounter = useQuery({
    queryKey: ['encounter', id],
    queryFn: async () => (await api.get<Encounter>(`/encounters/${id}`)).data,
  })

  useEffect(() => {
    if (encounter.data) {
      setForm({
        reason: encounter.data.reason ?? '',
        diagnosis: encounter.data.diagnosis ?? '',
        evolution: encounter.data.evolution ?? '',
        indications: encounter.data.indications ?? '',
      })
    }
  }, [encounter.data])

  const save = useMutation({
    mutationFn: async (close: boolean) => (await api.patch(`/encounters/${id}`, { ...form, close })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounter', id] })
      queryClient.invalidateQueries({ queryKey: ['encounters'] })
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  if (encounter.isLoading) return <Spinner />
  if (!encounter.data) return <p className="text-steel-600">Atención no encontrada.</p>

  const e = encounter.data
  const closed = e.status === 'CLOSED'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-steel-500">Atención clínica</p>
          <h1 className="font-display text-2xl font-bold text-ink">
            {e.patient ? `${e.patient.firstName} ${e.patient.lastName}` : 'Paciente'}
          </h1>
          <p className="text-sm text-steel-600">
            {e.patient ? formatRut(e.patient.rut) : ''}
            {e.appointment ? ` · ${e.appointment.service.name} · ${formatDateTime(e.appointment.startsAt)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={closed ? 'bg-gray-200 text-gray-700' : 'bg-brand-200 text-brand-800'}>
            {closed ? 'Cerrada' : 'Abierta'}
          </Badge>
          {e.patient && (
            <Link
              to={`/app/pacientes/${e.patientId}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-steel-200 bg-white px-3 py-2 text-sm font-medium text-steel-700 hover:border-brand-400 hover:bg-soft"
            >
              <Icon name="patients" className="h-4 w-4" />
              Ficha del paciente
            </Link>
          )}
        </div>
      </div>

      <Card title="Anamnesis" actions={<Link to="/app/plantillas" className="text-xs font-medium text-brand-700 hover:underline">Mis plantillas</Link>}>
        <AnamnesisForm encounter={e} />
      </Card>

      <Card title="Diagnósticos (CIE-10)">
        <DiagnosesSection encounterId={e.id} readOnly={closed} />
      </Card>

      <Card title="Indicaciones (receta, exámenes y kinesiología)">
        <ClinicalOrdersSection encounterId={e.id} readOnly={closed} />
      </Card>

      {/* El control indicado en el plan deja de ser una frase suelta: queda
          como pendiente para recepción y visible en el portal del paciente. */}
      <FollowUpsSection patientId={e.patientId} encounterId={e.id} readOnly={closed} />

      <PatientDocumentsSection patientId={e.patientId} />

      <Card title="Evolución e indicaciones">
        <div className="grid gap-3 lg:grid-cols-2">
          <Field label="Motivo de consulta">
            <Textarea rows={2} disabled={closed} value={form.reason} onChange={(ev) => setForm({ ...form, reason: ev.target.value })} />
          </Field>
          <Field label="Diagnóstico (resumen)">
            <Textarea rows={2} disabled={closed} value={form.diagnosis} onChange={(ev) => setForm({ ...form, diagnosis: ev.target.value })} />
          </Field>
          <Field label="Evolución / notas clínicas">
            <Textarea rows={5} disabled={closed} value={form.evolution} onChange={(ev) => setForm({ ...form, evolution: ev.target.value })} />
          </Field>
          <Field label="Indicaciones al paciente">
            <Textarea rows={5} disabled={closed} value={form.indications} onChange={(ev) => setForm({ ...form, indications: ev.target.value })} />
          </Field>
        </div>
        {!closed && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate(false)}>
              {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button variant="cta" disabled={save.isPending} onClick={() => save.mutate(true)}>
              Guardar y cerrar atención
            </Button>
          </div>
        )}
        {closed && (
          <p className="mt-3 text-xs text-steel-500">
            La atención está cerrada: la ficha ya no se edita (Ley 20.584). Cerrada el{' '}
            {e.closedAt ? formatDateTime(e.closedAt) : '—'}.
          </p>
        )}
        <ErrorText>{error}</ErrorText>
      </Card>
    </div>
  )
}
